import { normalizeStoredConfig } from "@/features/config-editor/utils/normalize-stored-config";
import { globalWebSocketManager } from "@/features/shot-tracking/services/global-websocket-manager";
import { storageManager } from "@/services/storage/storage/storage";

import type { ClientConfig } from "../types/client-config";
import { ensureVgImageCached } from "./vg-image-cache/vg-image-cache";

/**
 * Centralized Config Manager
 *
 * Single source of truth for all client configuration operations.
 * Ensures proper WebSocket reconnection when config changes.
 *
 * ALWAYS use this service for config operations to maintain consistency.
 */
class ConfigManager {
  /**
   * Initialize the config manager
   */
  initialize(): void {}

  /**
   * Get current client configuration
   */
  async getConfig(): Promise<ClientConfig | null> {
    await storageManager.initialize();
    const stored = await storageManager.getClientConfig();
    return normalizeStoredConfig(stored);
  }

  /**
   * Set/Update client configuration
   * Handles WebSocket reconnection with new config
   */
  async setConfig(config: ClientConfig): Promise<void> {
    try {
      const previous = await storageManager.getClientConfig();
      await storageManager.setClientConfig(config);

      const previousImageUrl = previous?.golfCourseConfig?.vgImageMap?.imageUrl;
      const nextImageUrl = config.golfCourseConfig?.vgImageMap?.imageUrl;
      if (
        config.golfCourseConfig?.mapProvider === "vg-image" &&
        nextImageUrl &&
        nextImageUrl !== previousImageUrl
      ) {
        void ensureVgImageCached(nextImageUrl);
      } else if (
        previousImageUrl &&
        previousImageUrl !== nextImageUrl &&
        !nextImageUrl
      ) {
        await storageManager.clearAllCourseImages();
      }

      if (config.radars.some((radar) => radar.webSocketBaseUrl)) {
        await globalWebSocketManager.refreshClientId();
      }
    } catch (error) {
      console.error("Failed to update configuration:", error);
      throw error;
    }
  }

  /**
   * Create a new local client configuration
   */
  async createLocalClient(
    config: Omit<ClientConfig, "clientId" | "registeredAt">
  ): Promise<ClientConfig> {
    const clientId = `local-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const localClient: ClientConfig = {
      ...config,
      clientId,
      registeredAt: Date.now(),
    };
    await this.setConfig(localClient);
    return localClient;
  }

  async selectExistingClient(config: ClientConfig): Promise<void> {
    await this.setConfig(config);
  }

  async updateConfig(config: ClientConfig): Promise<void> {
    const existingConfig = await this.getConfig();
    const updatedConfig: ClientConfig = {
      ...config,
      clientId: config.clientId || existingConfig?.clientId || "",
      registeredAt:
        config.registeredAt || existingConfig?.registeredAt || Date.now(),
    };

    await this.setConfig(updatedConfig);
  }

  /**
   * Clear client configuration
   */
  async clearConfig(): Promise<void> {
    try {
      await storageManager.clearClientConfig();
    } catch (error) {
      console.error("Failed to clear configuration:", error);
      throw error;
    }
  }

  /**
   * Check if client is registered
   */
  async isRegistered(): Promise<boolean> {
    const config = await this.getConfig();
    return !!config?.clientId?.trim();
  }
}

// Export singleton instance
export const configManager = new ConfigManager();
